import type { MouseEvent, ReactNode } from 'react';
import { useNavigate } from './router';

/**
 * An in-app link. It is a real `<a href>` first and a click handler second, which is what keeps
 * middle-click, ⌘/Ctrl-click, "copy link address" and a screen reader's link list all working —
 * a `<button>` styled as a link breaks every one of them.
 *
 * Renders props only (R13): the href arrives already built by `formatRoute`, never assembled here.
 */
export interface LinkProps {
  readonly href: string;
  readonly className?: string;
  readonly title?: string;
  readonly replace?: boolean;
  /**
   * These two exist for the *duplicate* link — a thumbnail and a title pointing at the same video
   * (the recommendations rail). Together they keep the second copy out of the tab order and out of
   * the accessibility tree, so a row is one link to a keyboard and a screen reader while still
   * being a big target for a mouse. `aria-hidden` on a focusable element would be a violation,
   * which is why they are documented as a pair.
   */
  readonly tabIndex?: number;
  readonly ariaHidden?: boolean;
  /** `aria-current="page"` — the shell's nav marks the destination you are already on. */
  readonly ariaCurrent?: boolean;
  readonly ariaLabel?: string;
  /**
   * Runs **before** the navigation and can claim the press by cancelling it: `isPlainLeftClick`
   * already declines an event whose default is prevented, so a handler that calls
   * `event.preventDefault()` stops this link navigating without needing a second flag.
   *
   * Added for the queue (2026-08-07), whose whole interception rule depends on the rest of this
   * component being untouched — every modifier the browser owns is still declined above, so
   * middle-click and ⌘-click open the video in a new tab whether or not a queue exists.
   */
  readonly onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  readonly children: ReactNode;
}

/** The modifier set the browser gives its own meaning to; we must not intercept any of them. */
function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  );
}

export function Link({
  href,
  className,
  title,
  replace,
  tabIndex,
  ariaHidden,
  ariaCurrent,
  ariaLabel,
  onClick,
  children,
}: LinkProps) {
  const navigate = useNavigate();

  return (
    <a
      href={href}
      className={className}
      title={title}
      tabIndex={tabIndex}
      aria-hidden={ariaHidden === true ? true : undefined}
      aria-current={ariaCurrent === true ? 'page' : undefined}
      aria-label={ariaLabel}
      onClick={(event) => {
        onClick?.(event);
        if (!isPlainLeftClick(event)) return;
        event.preventDefault();
        navigate(href, { replace });
      }}
    >
      {children}
    </a>
  );
}
